import { useCallback, useEffect, useState } from "react";
import {
  Eye,
  Megaphone,
  Pencil,
  Plus,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { useToast } from "../../../contexts/toast/toast-context";
import api from "../../../services/api";
import type {
  Campaign,
  CampaignPage,
  CampaignStatus,
} from "../../../types/campaign";
import { getApiErrorMessage } from "../../../utils";

const labels: Record<CampaignStatus, string> = {
  DRAFT: "Rascunho",
  SCHEDULED: "Agendada",
  IN_PROGRESS: "Em andamento",
  COMPLETED: "Concluída",
  CANCELLED: "Cancelada",
};
const badge: Record<CampaignStatus, string> = {
  DRAFT: "bg-slate-100 text-slate-700",
  SCHEDULED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-800",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  CANCELLED: "bg-red-100 text-red-700",
};
const formatDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: "America/Sao_Paulo",
      }).format(new Date(value))
    : "—";

export default function CampaignsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState<CampaignPage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelTarget, setCancelTarget] = useState<Campaign>();
  const [cancelling, setCancelling] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Campaign>();
  const [deleting, setDeleting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<CampaignPage>("/campaigns", {
        params: {
          page,
          limit: 10,
          search: search || undefined,
          status: status || undefined,
        },
      });
      setData(response.data);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);
  useEffect(() => {
    const timer = window.setTimeout(load, 250);
    return () => window.clearTimeout(timer);
  }, [load]);
  const cancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api.post(`/campaigns/${cancelTarget._id}/cancel`);
      toast.success("Campanha cancelada.");
      setCancelTarget(undefined);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setCancelling(false);
    }
  };
  const deleteDraft = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/campaigns/${deleteTarget._id}`);
      toast.success("Rascunho excluído.");
      setDeleteTarget(undefined);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };
  return (
    <section className="mx-auto max-w-[1100px] space-y-5 p-3 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-1 text-xs text-slate-500">Comercial / Campanhas</p>
          <h1 className="text-2xl font-bold text-slate-900">Campanhas</h1>
          <p className="mt-1 text-sm text-slate-600">
            Gerencie suas campanhas de WhatsApp
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/painel/comercial/campanhas/nova")}
        >
          <Plus size={18} /> Nova campanha
        </button>
      </div>
      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-bold">Suas campanhas</h2>
            <span className="text-xs text-slate-400">
              Dados atualizados em tempo real
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_220px]">
            <label className="relative">
              <span className="sr-only">Buscar por nome</span>
              <Search
                className="absolute left-3 top-2.5 text-slate-400"
                size={17}
              />
              <input
                className="form-input pl-10"
                placeholder="Buscar campanha..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </label>
            <label>
              <span className="sr-only">Filtrar por status</span>
              <select
                className="form-input"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Todos os status</option>
                {Object.entries(labels).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center">Carregando campanhas...</div>
        ) : error ? (
          <div
            role="alert"
            className="m-5 rounded-xl border border-red-200 bg-red-50 p-6 text-red-700"
          >
            {error}{" "}
            <button className="ml-2 underline" onClick={load}>
              Tentar novamente
            </button>
          </div>
        ) : !data?.data.length ? (
          <div className="p-12 text-center">
            <Megaphone className="mx-auto mb-3 text-slate-400" />
            <p className="font-medium">Nenhuma campanha encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[850px] text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  {[
                    "Campanha",
                    "Status",
                    "Destinatários",
                    "Progresso",
                    "Programada",
                    "Finalizada",
                    "Ações",
                  ].map((h) => (
                    <th className="px-4 py-3" key={h}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.map((item) => (
                  <tr className="border-t" key={item._id}>
                    <td className="px-4 py-4 font-bold text-slate-900">
                      {item.name}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full px-2 py-1 text-[10px] font-bold ${badge[item.status]}`}
                      >
                        {labels[item.status]}
                      </span>
                    </td>
                    <td className="px-4 py-4">{item.total_recipients}</td>
                    <td className="px-4 py-4">
                      {item.processed_recipients} / {item.total_recipients}
                    </td>
                    <td className="px-4 py-4">
                      {formatDate(item.scheduled_at)}
                    </td>
                    <td className="px-4 py-4">
                      {formatDate(item.completed_at || item.cancelled_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        {item.status === "DRAFT" ? (
                          <button
                            aria-label={`Continuar edição de ${item.name}`}
                            title="Continuar edição"
                            className="btn-icon h-8 w-8 bg-blue-50 text-blue-700 hover:bg-blue-100"
                            onClick={() =>
                              navigate(
                                `/painel/comercial/campanhas/nova?draft=${item._id}`,
                              )
                            }
                          >
                            <Pencil size={15} />
                          </button>
                        ) : (
                          <button
                            aria-label={`Abrir ${item.name}`}
                            title="Visualizar campanha"
                            className="btn-icon h-8 w-8 bg-slate-100"
                            onClick={() =>
                              navigate(
                                `/painel/comercial/campanhas/${item._id}`,
                              )
                            }
                          >
                            <Eye size={15} />
                          </button>
                        )}
                        {item.status === "DRAFT" && (
                          <button
                            aria-label={`Excluir rascunho ${item.name}`}
                            title="Excluir rascunho"
                            className="btn-icon h-8 w-8 bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => setDeleteTarget(item)}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                        {["SCHEDULED", "IN_PROGRESS"].includes(item.status) && (
                          <button
                            aria-label={`Cancelar ${item.name}`}
                            title="Cancelar campanha"
                            className="btn-icon h-8 w-8 bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => setCancelTarget(item)}
                          >
                            <XCircle size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {data && data.totalPages > 1 && (
        <div className="flex justify-end gap-2">
          <button
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => setPage((v) => v - 1)}
          >
            Anterior
          </button>
          <span className="px-3 py-2 text-sm">
            {page} de {data.totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page === data.totalPages}
            onClick={() => setPage((v) => v + 1)}
          >
            Próxima
          </button>
        </div>
      )}
      <ConfirmationModal
        isOpen={Boolean(cancelTarget)}
        title="Cancelar campanha?"
        description="Os destinatários ainda não processados não receberão a mensagem. Mensagens já enviadas não podem ser recuperadas."
        confirmLabel="Cancelar campanha"
        isLoading={cancelling}
        onCancel={() => setCancelTarget(undefined)}
        onConfirm={cancel}
      />
      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        title="Excluir rascunho?"
        description={`O rascunho${deleteTarget ? ` “${deleteTarget.name}”` : ""} será excluído permanentemente.`}
        confirmLabel="Excluir rascunho"
        isLoading={deleting}
        onCancel={() => setDeleteTarget(undefined)}
        onConfirm={deleteDraft}
      />
    </section>
  );
}
